// src/pages/pengamatan/sensus-populasi-anakan-pisang/pdfDownload.js
import React from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Autocomplete,
    TextField,
    InputAdornment,
    Popover,
    Box
} from "@mui/material";
import { Slide } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ClearIcon from '@mui/icons-material/Clear';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Hook untuk menangani download PDF dan dialog terkait
 */
export const usePdfDownload = (orders, onStatusUpdate) => {
    const [downloadDialogOpen, setDownloadDialogOpen] = React.useState(false);
    const [selectedLocation, setSelectedLocation] = React.useState('');
    const [locationInput, setLocationInput] = React.useState('');
    const [helpAnchorEl, setHelpAnchorEl] = React.useState(null);

    /**
     * Fungsi untuk mendapatkan daftar lokasi yang memiliki status Processed
     */
    const getProcessedLocations = () => {
        const processedLocations = new Set();
        
        orders.forEach(order => {
            if (order.status_pengamatan === "Processed" && order.block) {
                // Pastikan block tidak null/undefined dan trim whitespace
                const block = order.block.trim();
                if (block) {
                    processedLocations.add(block);
                }
            }
        });
        
        // Konversi ke array, filter null/undefined, dan sort
        return Array.from(processedLocations)
            .filter(location => location != null && location !== '')
            .sort();
    };

    /**
     * Fungsi untuk membuka dialog download
     */
    const handleOpenDownloadDialog = () => {
        setDownloadDialogOpen(true);
    };

    /**
     * Fungsi untuk menutup dialog download
     */
    const handleCloseDownloadDialog = () => {
        setDownloadDialogOpen(false);
        setSelectedLocation('');
        setLocationInput('');
        setHelpAnchorEl(null);
    };

    /**
     * Fungsi untuk handle download PDF
     */
    const handleDownloadPDF = () => {
        if (!selectedLocation) {
            onStatusUpdate({
                open: true,
                message: 'Pilih lokasi terlebih dahulu',
                severity: 'warning'
            });
            return;
        }

        // TODO: Implementasi download PDF akan ditambahkan nanti
        console.log('Download PDF untuk lokasi:', selectedLocation);
        
        // Simulasi download berhasil
        onStatusUpdate({
            open: true,
            message: `Berhasil mendownload form untuk lokasi ${selectedLocation}`,
            severity: 'success'
        });
        
        handleCloseDownloadDialog();
    };

    /**
     * Handler untuk perubahan input autocomplete
     */
    const handleLocationInputChange = (event, newValue) => {
        setSelectedLocation(newValue || '');
        setLocationInput(newValue || '');
    };

    /**
     * Handler untuk input text change
     */
    const handleInputChange = (event, newInputValue) => {
        setLocationInput(newInputValue || '');
    };

    /**
     * Handler untuk membuka popover bantuan
     */
    const handleHelpClick = (event) => {
        setHelpAnchorEl(event.currentTarget);
    };

    /**
     * Handler untuk menutup popover bantuan
     */
    const handleHelpClose = () => {
        setHelpAnchorEl(null);
    };

    /**
     * Handler untuk clear input
     */
    const handleClearInput = () => {
        setSelectedLocation('');
        setLocationInput('');
    };

    const helpOpen = Boolean(helpAnchorEl);

    return {
        downloadDialogOpen,
        selectedLocation,
        locationInput,
        setSelectedLocation,
        setLocationInput,
        getProcessedLocations,
        handleOpenDownloadDialog,
        handleCloseDownloadDialog,
        handleDownloadPDF,
        handleLocationInputChange,
        handleInputChange,
        helpAnchorEl,
        handleHelpClick,
        handleHelpClose,
        handleClearInput,
        helpOpen
    };
};

/**
 * Komponen Popover untuk Informasi Bantuan
 */
const HelpPopover = ({ anchorEl, onClose, open }) => {
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            sx={{
                '& .MuiPopover-paper': {
                    padding: 2,
                    maxWidth: 300,
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }
            }}
        >
            <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Catatan:
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    • Hanya menampilkan lokasi dengan status pengamatan <strong>Processed</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    • Ketik nama lokasi untuk mencari lebih cepat
                </Typography>
                <Typography variant="body2">
                    • Pilih dari daftar yang muncul atau ketik manual
                </Typography>
            </Box>
        </Popover>
    );
};

/**
 * Komponen Dialog untuk Download Form PDF (Full-screen dengan Autocomplete)
 */
export const PdfDownloadDialog = ({
    open,
    selectedLocation,
    locationInput,
    onClose,
    onLocationChange,
    onInputChange,
    onDownload,
    processedLocations,
    helpAnchorEl,
    onHelpClick,
    onHelpClose,
    onClearInput,
    helpOpen
}) => {
    
    // Filter options untuk memastikan tidak ada null/undefined
    const filteredOptions = React.useMemo(() => {
        return processedLocations.filter(option => 
            option != null && option !== '' && typeof option === 'string'
        );
    }, [processedLocations]);

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
        >
            <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        Download Form
                    </Typography>
                    <Button 
                        autoFocus 
                        color="inherit" 
                        onClick={onDownload}
                        disabled={!selectedLocation}
                    >
                        Download
                    </Button>
                </Toolbar>
            </AppBar>
            
            <DialogContent sx={{ p: 3 }}>
                <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
<Autocomplete
    freeSolo
    value={selectedLocation || ''}
    inputValue={locationInput || ''}
    onInputChange={onInputChange}
    onChange={onLocationChange}
    options={filteredOptions}
    clearOnBlur={false}
    disableClearable={true} // Nonaktifkan clear button bawaan
    renderInput={(params) => (
        <TextField
            {...params}
            label="Pilih Lokasi"
            variant="outlined"
            placeholder="Ketik atau pilih lokasi..."
            InputProps={{
                ...params.InputProps,
                endAdornment: (
                    <React.Fragment>
                        {/* TOMBOL CLEAR MANUAL */}
                        {selectedLocation && (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={onClearInput}
                                    edge="end"
                                    title="Clear"
                                    sx={{ mr: 0.5 }}
                                >
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )}
                        {/* TOMBOL BANTUAN */}
                        <InputAdornment position="end">
                            <IconButton
                                onClick={onHelpClick}
                                edge="end"
                                title="Informasi bantuan"
                                sx={{ marginRight: 0 }}
                            >
                                <HelpOutlineIcon />
                            </IconButton>
                        </InputAdornment>
                        {/* End adornment default dari Autocomplete */}
                        {params.InputProps.endAdornment}
                    </React.Fragment>
                ),
            }}
        />
    )}
    noOptionsText="Tidak ada lokasi dengan status Processed"
    sx={{
        '& .MuiAutocomplete-inputRoot': {
            padding: '8px 12px',
        }
    }}
    filterOptions={(options, state) => {
        const inputValue = state.inputValue || '';
        return options.filter(option =>
            option && 
            typeof option === 'string' && 
            option.toLowerCase().includes(inputValue.toLowerCase())
        );
    }}
/>
                </FormControl>

                {filteredOptions.length === 0 && (
                    <DialogContentText sx={{ mt: 3, color: 'warning.main' }}>
                        <strong>Tidak ada lokasi dengan status Processed.</strong><br />
                        Pastikan terdapat data dengan status Processed sebelum mendownload form.
                    </DialogContentText>
                )}

                {/* Popover Bantuan */}
                <HelpPopover 
                    anchorEl={helpAnchorEl}
                    onClose={onHelpClose}
                    open={helpOpen}
                />
            </DialogContent>
        </Dialog>
    );
};

export default usePdfDownload;